import type {
	CreateProjectDocumentInput,
	GetProjectDocumentResponse,
	ListProjectDocumentsResponse,
	ProjectDocumentListItem,
} from "@shared/types";
import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

function buildDocumentFormData(data: CreateProjectDocumentInput): FormData {
	const formData = new FormData();
	formData.append("title", data.title);
	formData.append("type", data.type);
	if (data.description) {
		formData.append("description", data.description);
	}
	formData.append("file", data.file as Blob);
	return formData;
}

async function fetchDocumentBlob(url: string): Promise<Blob> {
	const response = await apiClient.get(url, { responseType: "blob" });
	return response.data as Blob;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
	const objectUrl = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = objectUrl;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
}

function openBlobInNewTab(blob: Blob): void {
	const objectUrl = window.URL.createObjectURL(blob);
	window.open(objectUrl, "_blank", "noopener,noreferrer");
	window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
}

export async function getProjectDocuments(
	projectId: string,
): Promise<ListProjectDocumentsResponse> {
	const response = await apiClient.get(
		`${API_BASE}/projects/${projectId}/documents`,
	);
	return response.data.data;
}

export async function getProjectDocument(
	projectId: string,
	documentId: string,
): Promise<GetProjectDocumentResponse> {
	const response = await apiClient.get(
		`${API_BASE}/projects/${projectId}/documents/${documentId}`,
	);
	return response.data.data;
}

export async function createProjectDocument(
	projectId: string,
	data: CreateProjectDocumentInput,
): Promise<GetProjectDocumentResponse> {
	const response = await apiClient.post(
		`${API_BASE}/projects/${projectId}/documents`,
		buildDocumentFormData(data),
	);
	return response.data.data;
}

export async function createProjectDocumentVersion(
	projectId: string,
	documentId: string,
	file: File,
): Promise<GetProjectDocumentResponse> {
	const formData = new FormData();
	formData.append("file", file);
	const response = await apiClient.post(
		`${API_BASE}/projects/${projectId}/documents/${documentId}/versions`,
		formData,
	);
	return response.data.data;
}

export async function openProjectDocument(downloadUrl: string): Promise<void> {
	const blob = await fetchDocumentBlob(downloadUrl);
	openBlobInNewTab(blob);
}

export async function downloadProjectDocument(
	downloadUrl: string,
	filename: string,
): Promise<void> {
	const blob = await fetchDocumentBlob(downloadUrl);
	triggerBlobDownload(blob, filename);
}

export type { ProjectDocumentListItem };
